import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiCreateDocumentGenerationJob,
  CompanyService,
  DocumentsService,
  FileCreateRequest,
  FirmDocumentReferenceTransferRequest,
} from 'api';
import type {
  FileCreateResponse,
  FirmDocumentReference,
  FirmDocumentReferenceBulkDeleteResponse,
  FirmDocumentReferenceTransferResponse,
  FolderView,
  RetrieveDocumentGenerationJob,
} from 'api';
import FilesService from 'services/documents';
import { POLL_TYPE, useAppLoadAsyncOperations } from 'services/hooks/useAppLoadAsyncActions';
import { useResponse } from 'services/hooks/useResponse';
import { Logger } from 'utilities';
import type {
  FirmDocumentAnalyzeParams,
  FirmDocumentBulkDeleteParams,
  FirmDocumentCopyParams,
  FirmDocumentDownloadParams,
  FirmDocumentDownloadTarget,
  FirmDocumentEditParams,
  FirmDocumentFolderCreateParams,
  FirmDocumentFolderDeleteParams,
  FirmDocumentFolderRenameParams,
  FirmDocumentMoveParams,
  FirmDocumentUploadParams,
  UseFirmDocumentActionsReturn,
} from './useFirmDocumentActions.types';
import { isFirmLevelDocumentsId } from '../firmLevelDocuments';
import { FIRM_DOCUMENT_COMPANIES_QUERY_KEY } from '../useFirmDocumentCompaniesQuery/useFirmDocumentCompaniesQuery.constants';
import { FIRM_DOCUMENT_FILTER_OPTIONS_QUERY_KEY } from '../useFirmDocumentFilterOptionsQuery/useFirmDocumentFilterOptionsQuery.constants';
import { FIRM_DOCUMENT_FOLDERS_QUERY_KEY } from '../useFirmDocumentFoldersQuery/useFirmDocumentFoldersQuery.constants';
import {
  FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_KEY,
} from '../useFirmDocumentMeasurementDateDocumentsQuery/useFirmDocumentMeasurementDateDocumentsQuery.constants';
import {
  FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_KEY,
} from '../useFirmDocumentMeasurementDatesQuery/useFirmDocumentMeasurementDatesQuery.constants';
import { FIRM_DOCUMENT_REQUESTED_FILES_QUERY_KEY } from '../useFirmDocumentRequestedFilesQuery/useFirmDocumentRequestedFilesQuery.constants';
import { FIRM_DOCUMENTS_QUERY_KEY } from '../useFirmDocumentsQuery/useFirmDocumentsQuery.constants';
import { FIRM_DOCUMENT_SUGGESTIONS_QUERY_KEY } from '../useFirmDocumentSuggestionsQuery/useFirmDocumentSuggestionsQuery.constants';
import { FIRM_LEVEL_FOLDERS_QUERY_KEY, FIRM_LEVEL_SUMMARY_QUERY_KEY } from '../useFirmFolders/useFirmFolders.constants';

interface FirmDocumentPresignedUploadResponse {
  auto_classify?: boolean;
  key?: string;
  url?: string;
}

const getBulkFileLabel = (count: number) => (count === 1 ? 'document' : `${count} documents`);

const FIRM_DOCUMENT_CAP_TABLE_PARSER_OVERRIDES = [
  {
    file_type: 'xlsx',
    minify_json: true,
    xlsx_parsing_mode: 'JSON_CONVERSION',
  },
];

const getAnalyzeSuccessLabel = (processingType: FirmDocumentAnalyzeParams['processingType']) => (
  processingType === ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE
    ? 'Cap Table analysis'
    : 'Financials analysis'
);

const getAnalyzeParserOverrides = (processingType: FirmDocumentAnalyzeParams['processingType']) => (
  processingType === ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE
    ? FIRM_DOCUMENT_CAP_TABLE_PARSER_OVERRIDES
    : []
);

const triggerBlobDownload = ({ blob, fileName }: { blob: Blob; fileName: string }) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const triggerSignedUrlDownload = async ({ fileName, fileId, firmId }: FirmDocumentDownloadTarget) => {
  const response = await DocumentsService.apiDocumentsDownloadRead(
    String(fileId),
    undefined,
    firmId ? String(firmId) : undefined
  );

  if (!response.signed_url) {
    throw new Error(`Missing signed URL for document ${fileId}`);
  }

  const fileResponse = await fetch(response.signed_url);

  if (!fileResponse.ok) {
    throw new Error(`Download failed for document ${fileId}: ${fileResponse.status}`);
  }

  triggerBlobDownload({
    blob: await fileResponse.blob(),
    fileName,
  });
};

const triggerSequentialSignedUrlDownloads = (documents: FirmDocumentDownloadTarget[]) => documents.reduce(
  (previousDownload, document) => previousDownload.then(() => triggerSignedUrlDownload(document)),
  Promise.resolve()
);

const getFileExtension = (file: File) => file.name.split('.').pop()?.toLowerCase() ?? '';

export const getBaseFileName = (file: File, fileType: string) => {
  const extensionSuffix = `.${fileType}`;

  return file.name.toLowerCase().endsWith(extensionSuffix)
    ? file.name.slice(0, -extensionSuffix.length)
    : file.name;
};

export const getSupportedFileType = (file: File): FileCreateRequest.file_type => {
  const extension = getFileExtension(file);
  const supportedFileTypes = Object.values(FileCreateRequest.file_type);

  if (!supportedFileTypes.includes(extension as FileCreateRequest.file_type)) {
    throw new Error(`Unsupported document file type: ${extension || 'unknown'}`);
  }

  return extension as FileCreateRequest.file_type;
};

export const getUploadKeyParts = (key: string) => {
  const keyParts = key.split('/');
  const filenamePart = keyParts.at(-1) ?? '';
  const guid = filenamePart.split('.').slice(0, -1).join('.');
  const filePath = keyParts.slice(0, -1).join('/');

  if (!guid || !filePath) {
    throw new Error('Invalid upload key returned for document upload.');
  }

  return { filePath, guid };
};

export const uploadFileToSignedUrl = async ({ file, url }: { file: File; url: string }) => {
  const response = await fetch(url, {
    body: file,
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error(`Document upload failed with status ${response.status}.`);
  }
};

const createFirmDocumentUpload = async ({
  autoClassify = false,
  file,
  filesService,
  firmId,
  folderId = null,
  mdDocumentsId,
  measurementDateId,
}: FirmDocumentUploadParams & { filesService: FilesService }): Promise<FileCreateResponse> => {
  const fileType = getSupportedFileType(file);

  // Firm Documents: firm-level upload endpoints, no measurement date.
  if (isFirmLevelDocumentsId(mdDocumentsId) && firmId) {
    const { url, key } = await DocumentsService.apiDocumentsFirmsFirmFilesUploadUrlList(String(firmId), fileType);
    await uploadFileToSignedUrl({ file, url });
    const { filePath, guid } = getUploadKeyParts(key);
    return DocumentsService.apiDocumentsFirmsFirmFilesCreate(String(firmId), {
      file_path: filePath,
      file_type: fileType,
      filename: getBaseFileName(file, fileType),
      folder_id: folderId,
      guid,
    }) as unknown as FileCreateResponse;
  }

  const presignedResponse = await filesService.getPreSignedUrl(
    String(measurementDateId),
    fileType,
    autoClassify
  ) as FirmDocumentPresignedUploadResponse;

  if (!presignedResponse.url || !presignedResponse.key) {
    throw new Error('Missing signed URL for document upload.');
  }

  await uploadFileToSignedUrl({
    file,
    url: presignedResponse.url,
  });

  const { filePath, guid } = getUploadKeyParts(presignedResponse.key);

  return DocumentsService.apiDocumentsFilesCreate(String(mdDocumentsId), {
    auto_classify: presignedResponse.auto_classify ?? autoClassify,
    file_path: filePath,
    file_type: fileType,
    filename: getBaseFileName(file, fileType),
    folder_id: folderId,
    guid,
    md_documents_id: mdDocumentsId,
  });
};

export const useFirmDocumentActions = (): UseFirmDocumentActionsReturn => {
  const queryClient = useQueryClient();
  const filesService = useMemo(() => new FilesService(), []);
  const { manuallyExecutePoll } = useAppLoadAsyncOperations(false);
  const { processErrorResponse, successNotification } = useResponse();

  const invalidateFirmDocumentQueries = async (firmId?: number | null) => {
    const firmDocumentsQueryKey = firmId ? [FIRM_DOCUMENTS_QUERY_KEY, firmId] : [FIRM_DOCUMENTS_QUERY_KEY];
    const firmCompaniesQueryKey = firmId
      ? [FIRM_DOCUMENT_COMPANIES_QUERY_KEY, firmId]
      : [FIRM_DOCUMENT_COMPANIES_QUERY_KEY];
    const firmFilterOptionsQueryKey = firmId
      ? [FIRM_DOCUMENT_FILTER_OPTIONS_QUERY_KEY, firmId]
      : [FIRM_DOCUMENT_FILTER_OPTIONS_QUERY_KEY];
    const firmSuggestionsQueryKey = firmId
      ? [FIRM_DOCUMENT_SUGGESTIONS_QUERY_KEY, firmId]
      : [FIRM_DOCUMENT_SUGGESTIONS_QUERY_KEY];
    const firmFoldersQueryKey = firmId
      ? [FIRM_DOCUMENT_FOLDERS_QUERY_KEY, firmId]
      : [FIRM_DOCUMENT_FOLDERS_QUERY_KEY];
    const firmRequestedFilesQueryKey = firmId
      ? [FIRM_DOCUMENT_REQUESTED_FILES_QUERY_KEY, firmId]
      : [FIRM_DOCUMENT_REQUESTED_FILES_QUERY_KEY];

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: firmFoldersQueryKey }),
      queryClient.invalidateQueries({ queryKey: [FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: firmDocumentsQueryKey }),
      queryClient.invalidateQueries({ queryKey: firmCompaniesQueryKey }),
      queryClient.invalidateQueries({ queryKey: firmFilterOptionsQueryKey }),
      queryClient.invalidateQueries({ queryKey: firmRequestedFilesQueryKey }),
      queryClient.invalidateQueries({ queryKey: firmSuggestionsQueryKey }),
      queryClient.invalidateQueries({ queryKey: [FIRM_LEVEL_FOLDERS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FIRM_LEVEL_SUMMARY_QUERY_KEY] }),
    ]);
  };

  const downloadDocumentsMutation = useMutation<void, Error, FirmDocumentDownloadParams>({
    mutationFn: ({ documents }) => triggerSequentialSignedUrlDownloads(documents),
    onError: (error, { documents }) => {
      Logger.error('Error downloading firm documents', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while downloading ${getBulkFileLabel(documents.length)}.`,
      });
    },
    onSuccess: (_response, { documents }) => {
      successNotification(`${getBulkFileLabel(documents.length)} downloaded successfully.`);
    },
  });

  const renameFolderMutation = useMutation<
    Awaited<ReturnType<typeof DocumentsService.apiDocumentsFolderUpdate>>,
    Error,
    FirmDocumentFolderRenameParams
  >({
    mutationFn: ({ firmId, folderId, folderName, mdDocumentsId, parentFolderId = null }) =>
      isFirmLevelDocumentsId(mdDocumentsId)
        ? (DocumentsService.apiDocumentsFirmsFirmFoldersPartialUpdate(String(firmId), String(folderId), {
          name: folderName,
          parent_folder_id: parentFolderId,
        }) as unknown as Promise<Awaited<ReturnType<typeof DocumentsService.apiDocumentsFolderUpdate>>>)
        : DocumentsService.apiDocumentsFolderUpdate(String(mdDocumentsId), String(folderId), {
          md_documents: mdDocumentsId,
          name: folderName,
          parent_folder: parentFolderId,
        }),
    onError: (error, { folderName }) => {
      Logger.error('Error renaming firm document folder', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while renaming ${folderName}.`,
      });
    },
    onSuccess: async (_response, { firmId, folderName }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${folderName} folder was renamed successfully.`);
    },
  });

  const createFolderMutation = useMutation<FolderView, Error, FirmDocumentFolderCreateParams>({
    // The generated folder-create method currently omits the request body, so keep this behind the service hook.
    mutationFn: ({ firmId, folderName, mdDocumentsId, parentFolderId = null }) =>
      isFirmLevelDocumentsId(mdDocumentsId)
        ? (DocumentsService.apiDocumentsFirmsFirmFoldersCreate(String(firmId), {
          name: folderName,
          parent_folder_id: parentFolderId,
        }) as unknown as Promise<FolderView>)
        : (filesService.createSubFolder(String(mdDocumentsId), {
          md_documents_id: mdDocumentsId,
          name: folderName,
          parent_folder_id: parentFolderId,
        }) as Promise<FolderView>),
    onError: (error, { folderName }) => {
      Logger.error('Error creating firm document folder', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while creating ${folderName}.`,
      });
    },
    onSuccess: async (_response, { firmId, folderName }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${folderName} folder was created successfully.`);
    },
  });

  const deleteFolderMutation = useMutation<void, Error, FirmDocumentFolderDeleteParams>({
    mutationFn: ({ firmId, folderId, mdDocumentsId }) =>
      isFirmLevelDocumentsId(mdDocumentsId)
        ? DocumentsService.apiDocumentsFirmsFirmFoldersDelete(String(firmId), String(folderId))
        : DocumentsService.apiDocumentsFolderDelete(String(mdDocumentsId), String(folderId)),
    onError: (error, { folderName }) => {
      Logger.error('Error deleting firm document folder', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while deleting ${folderName}.`,
      });
    },
    onSuccess: async (_response, { firmId, folderName }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${folderName} folder was deleted successfully.`);
    },
  });

  const deleteDocumentReferencesMutation = useMutation<
    FirmDocumentReferenceBulkDeleteResponse,
    Error,
    FirmDocumentBulkDeleteParams
  >({
    mutationFn: ({ documentReferenceIds, firmId }) =>
      DocumentsService.apiDocumentsFirmsDocumentReferencesBulkDeleteCreate(String(firmId), {
        document_reference_ids: documentReferenceIds,
      }),
    onError: (error, { documentReferenceIds }) => {
      Logger.error('Error deleting firm document references', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while removing ${getBulkFileLabel(documentReferenceIds.length)}.`,
      });
    },
    onSuccess: async (_response, { documentReferenceIds, firmId }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${getBulkFileLabel(documentReferenceIds.length)} removed successfully.`);
    },
  });

  const editDocumentReferenceMutation = useMutation<FirmDocumentReference, Error, FirmDocumentEditParams>({
    mutationFn: ({
      companyId,
      documentReferenceId,
      fileName,
      firmId,
      measurementDateId,
      referenceObjectTypes,
      requestedFileIds,
    }) => DocumentsService.apiDocumentsFirmsDocumentReferencesPartialUpdate(
      String(firmId),
      String(documentReferenceId),
      // A firm document has no measurement date: only its name can change.
      isFirmLevelDocumentsId(measurementDateId)
        ? { filename: fileName }
        : {
          company_id: companyId,
          filename: fileName,
          measurement_date_id: measurementDateId,
          reference_object_types: referenceObjectTypes,
          requested_file_ids: requestedFileIds,
        }
    ),
    onError: (error, { fileName }) => {
      Logger.error('Error editing firm document reference', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while saving ${fileName || 'the document'}.`,
      });
    },
    onSuccess: async (_response, { fileName, firmId }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${fileName || 'Document'} was saved successfully.`);
    },
  });

  const uploadDocumentMutation = useMutation<FileCreateResponse, Error, FirmDocumentUploadParams>({
    mutationFn: params => createFirmDocumentUpload({
      ...params,
      filesService,
    }),
    onError: (error, { file }) => {
      Logger.error('Error uploading firm document', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while uploading ${file.name}.`,
      });
    },
    onSuccess: async (_response, { file, firmId, suppressQueryInvalidation, suppressSuccessNotification }) => {
      if (!suppressQueryInvalidation) {
        await invalidateFirmDocumentQueries(firmId);
      }

      if (!suppressSuccessNotification) {
        successNotification(`${file.name} was uploaded successfully.`);
      }
    },
  });

  const copyDocumentReferencesMutation = useMutation<
    FirmDocumentReferenceTransferResponse,
    Error,
    FirmDocumentCopyParams
  >({
    mutationFn: ({ documentReferenceIds, firmId, targetMeasurementDateId }) =>
      DocumentsService.apiDocumentsFirmsDocumentReferencesTransferCreate(String(firmId), {
        document_reference_ids: documentReferenceIds,
        mode: FirmDocumentReferenceTransferRequest.mode.COPY,
        target_measurement_date_id: targetMeasurementDateId,
      }),
    onError: (error, { documentReferenceIds, suppressErrorNotification }) => {
      Logger.error('Error copying firm document references', error);
      if (suppressErrorNotification) {
        return;
      }

      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while copying ${getBulkFileLabel(documentReferenceIds.length)}.`,
      });
    },
    onSuccess: async (_response, { documentReferenceIds, firmId }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${getBulkFileLabel(documentReferenceIds.length)} copied successfully.`);
    },
  });

  const moveDocumentReferencesMutation = useMutation<
    FirmDocumentReferenceTransferResponse,
    Error,
    FirmDocumentMoveParams
  >({
    mutationFn: ({
      documentReferenceIds,
      firmId,
      targetFolderId = null,
      targetMeasurementDateId,
    }) => DocumentsService.apiDocumentsFirmsDocumentReferencesTransferCreate(
      String(firmId),
      // Firm documents move between Firm Documents folders only.
      isFirmLevelDocumentsId(targetMeasurementDateId)
        ? {
          document_reference_ids: documentReferenceIds,
          mode: FirmDocumentReferenceTransferRequest.mode.MOVE,
          target_firm_level: true,
          target_folder_id: targetFolderId,
        }
        : {
          document_reference_ids: documentReferenceIds,
          mode: FirmDocumentReferenceTransferRequest.mode.MOVE,
          target_folder_id: targetFolderId,
          target_measurement_date_id: targetMeasurementDateId,
        }
    ),
    onError: (error, { documentReferenceIds, suppressErrorNotification }) => {
      Logger.error('Error moving firm document references', error);
      if (suppressErrorNotification) {
        return;
      }

      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while moving ${getBulkFileLabel(documentReferenceIds.length)}.`,
      });
    },
    onSuccess: async (_response, { documentReferenceIds, firmId }) => {
      await invalidateFirmDocumentQueries(firmId);
      successNotification(`${getBulkFileLabel(documentReferenceIds.length)} moved successfully.`);
    },
  });

  const analyzeDocumentsMutation = useMutation<
    RetrieveDocumentGenerationJob,
    Error,
    FirmDocumentAnalyzeParams
  >({
    mutationFn: ({ companyId, fileIds, processingType }) =>
      CompanyService.apiCompanyDocumentGenerationJobsNewCreate(String(companyId), {
        desired_termination_state: 'COMPLETE',
        parser_overrides: getAnalyzeParserOverrides(processingType),
        processing_type: processingType,
        reference_files: fileIds,
      }),
    onError: (error, { fileIds, processingType }) => {
      Logger.error('Error creating firm document analysis job', error);
      processErrorResponse({
        error,
        defaultErrorMessage: `An error occurred while starting ${getAnalyzeSuccessLabel(processingType).toLowerCase()} for ${getBulkFileLabel(fileIds.length)}.`,
      });
    },
    onSuccess: (_response, { fileIds, processingType }) => {
      manuallyExecutePoll(POLL_TYPE.GENERATION_JOBS);
      successNotification(`${getAnalyzeSuccessLabel(processingType)} started for ${getBulkFileLabel(fileIds.length)}.`);
    },
  });

  return {
    analyzeDocuments: analyzeDocumentsMutation.mutateAsync,
    copyDocumentReferences: copyDocumentReferencesMutation.mutateAsync,
    createFolder: createFolderMutation.mutateAsync,
    deleteFolder: deleteFolderMutation.mutateAsync,
    deleteDocumentReferences: deleteDocumentReferencesMutation.mutateAsync,
    downloadDocuments: downloadDocumentsMutation.mutateAsync,
    editDocumentReference: editDocumentReferenceMutation.mutateAsync,
    isAnalyzingDocuments: analyzeDocumentsMutation.isPending,
    isCopyingDocumentReferences: copyDocumentReferencesMutation.isPending,
    isCreatingFolder: createFolderMutation.isPending,
    isDeletingFolder: deleteFolderMutation.isPending,
    isDeletingDocumentReferences: deleteDocumentReferencesMutation.isPending,
    isDownloadingDocuments: downloadDocumentsMutation.isPending,
    isEditingDocumentReference: editDocumentReferenceMutation.isPending,
    isMovingDocumentReferences: moveDocumentReferencesMutation.isPending,
    isRenamingFolder: renameFolderMutation.isPending,
    isUploadingDocument: uploadDocumentMutation.isPending,
    moveDocumentReferences: moveDocumentReferencesMutation.mutateAsync,
    renameFolder: renameFolderMutation.mutateAsync,
    uploadDocument: uploadDocumentMutation.mutateAsync,
  };
};
